import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// Layouts
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import DashboardLayout from '@/layouts/DashboardLayout.vue'
import ChannelLayout from '@/layouts/ChannelLayout.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'
import AdminLayout from '@/layouts/AdminLayout.vue'

const routes: RouteRecordRaw[] = [
  // ========================================
  // ROTAS PÚBLICAS (Layout Default)
  // ========================================
  {
    path: '/',
    component: DefaultLayout,
    children: [
      {
        path: '',
        name: 'home',
        component: () => import('@/pages/index.vue'),
        meta: { title: 'Ergonlider - Consultoria em Ergonomia e Gestão Psicossocial' }
      },
      {
        path: 'sobre',
        name: 'about',
        component: () => import('@/pages/sobre.vue'),
        meta: { title: 'Sobre Nós - Ergonlider' }
      },
      {
        path: 'servicos',
        name: 'services',
        component: () => import('@/pages/servicos/index.vue'),
        meta: { title: 'Nossos Serviços - Ergonlider' }
      },
      {
        path: 'servicos/:slug',
        name: 'service-detail',
        component: () => import('@/pages/servicos/[slug].vue'),
        meta: { title: 'Serviço - Ergonlider' }
      },
      {
        path: 'loja',
        name: 'shop',
        component: () => import('@/pages/loja/index.vue'),
        meta: { title: 'Loja - Ergonlider' }
      },
      {
        path: 'loja/:slug',
        name: 'product-detail',
        component: () => import('@/pages/loja/[slug].vue'),
        meta: { title: 'Produto - Ergonlider' }
      },
      {
        path: 'checkout',
        name: 'checkout',
        component: () => import('@/pages/checkout.vue'),
        meta: {
          title: 'Checkout - Ergonlider',
          requiresAuth: true
        }
      },
      {
        path: 'contato',
        name: 'contact',
        component: () => import('@/pages/contato.vue'),
        meta: { title: 'Contato - Ergonlider' }
      },
      {
        path: 'noticias',
        name: 'news-list',
        component: () => import('@/pages/noticias/index.vue'),
        meta: { title: 'Notícias - Ergonlider' }
      },
      {
        path: 'noticias/:slug',
        name: 'news-detail',
        component: () => import('@/pages/noticias/[slug].vue'),
        meta: { title: 'Notícia - Ergonlider' }
      },
      {
        path: 'brand',
        name: 'brand',
        component: () => import('@/pages/brand.vue'),
        meta: { title: 'Brand Guidelines - Ergonlider' }
      },
            {
        path: 'pagamento/sucesso',
        name: 'payment-success',
        component: () => import('@/pages/pagamento/sucesso.vue'),
        meta: { title: 'Pagamento Confirmado - Ergonlider' }
      },
      {
        path: 'pagamento/cancelado',
        name: 'payment-cancelled',
        component: () => import('@/pages/pagamento/cancelado.vue'),
        meta: { title: 'Pagamento Cancelado - Ergonlider' }
      },
    ]
  },

  // ========================================
  // AUTENTICAÇÃO (Auth Layout - sem header/footer)
  // ========================================
  {
    path: '/',
    component: AuthLayout,
    children: [
      {
        path: 'login',
        name: 'login',
        component: () => import('@/pages/login.vue'),
        meta: {
          title: 'Login - Ergonlider',
          guestOnly: true
        }
      },
      {
        path: 'registro',
        name: 'register',
        component: () => import('@/pages/registro.vue'),
        meta: {
          title: 'Criar Conta - Ergonlider',
          guestOnly: true
        }
      },
      {
        path: 'recuperar-senha',
        name: 'forgot-password',
        component: () => import('@/pages/recuperar-senha.vue'),
        meta: {
          title: 'Recuperar Senha - Ergonlider',
          guestOnly: true
        }
      },
      {
        path: 'reset-password',
        name: 'reset-password',
        component: () => import('@/pages/reset-password.vue'),
        meta: { title: 'Redefinir Senha - Ergonlider' }
      },
    ]
  },

  // ========================================
  // ÁREA DO CLIENTE (Dashboard Layout)
  // ========================================
  {
    path: '/cliente',
    component: DashboardLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/pages/cliente/index.vue'),
        meta: { title: 'Minha Conta - Ergonlider' }
      },
      {
        path: 'produtos',
        name: 'my-products',
        component: () => import('@/pages/cliente/produtos.vue'),
        meta: { title: 'Meus Produtos - Ergonlider' }
      },
      {
        path: 'produtos/:slug',
        name: 'product-access',
        component: () => import('@/pages/cliente/produtos/[slug].vue'),
        meta: { title: 'Acessar Produto - Ergonlider' }
      },
      {
        path: 'videos',
        name: 'my-videos',
        component: () => import('@/pages/cliente/videos.vue'),
        meta: { title: 'Vídeos Educativos - Ergonlider' }
      },
      {
        path: 'financeiro',
        name: 'financial',
        component: () => import('@/pages/cliente/financeiro.vue'),
        meta: { title: 'Histórico Financeiro - Ergonlider' }
      },
      {
        path: 'canal-denuncias',
        name: 'my-channel',
        component: () => import('@/pages/cliente/canal-denuncias/index.vue'),
        meta: { title: 'Canal de Denúncias - Ergonlider' }
      },
      {
        path: 'canal-denuncias/configurar',
        name: 'channel-config',
        component: () => import('@/pages/cliente/canal-denuncias/configurar.vue'),
        meta: { title: 'Configurar Canal - Ergonlider' }
      },
      {
        path: 'canal-denuncias/:id',
        name: 'complaint-detail',
        component: () => import('@/pages/cliente/canal-denuncias/[id].vue'),
        meta: { title: 'Detalhes da Denúncia - Ergonlider' }
      },
      {
        path: 'perfil',
        name: 'profile',
        component: () => import('@/pages/cliente/perfil.vue'),
        meta: { title: 'Meu Perfil - Ergonlider' }
      },
      {
        path: 'pedidos',
        name: 'orders',
        component: () => import('@/pages/cliente/pedidos.vue'),
        meta: { title: 'Meus Pedidos - Ergonlider' }
      },
    ]
  },

  // ========================================
  // CANAL DE DENÚNCIAS PÚBLICO (White-label)
  // ========================================
  {
    path: '/canal/:slug',
    component: ChannelLayout,
    children: [
      {
        path: '',
        name: 'public-channel',
        component: () => import('@/pages/canal/[slug].vue'),
        meta: { title: 'Canal de Denúncias' }
      },
      {
        path: 'enviado',
        name: 'complaint-sent',
        component: () => import('@/pages/canal/enviado.vue'),
        meta: { title: 'Denúncia Enviada' }
      },
      {
        path: 'consultar',
        name: 'check-complaint',
        component: () => import('@/pages/canal/consultar.vue'),
        meta: { title: 'Consultar Denúncia' }
      },
    ]
  },

  // ========================================
  // ÁREA ADMINISTRATIVA (Admin Layout)
  // ========================================
  {
    path: '/admin',
    component: AdminLayout,
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      {
        path: '',
        name: 'admin-dashboard',
        component: () => import('@/pages/admin/index.vue'),
        meta: { title: 'Dashboard - Admin Ergonlider' }
      },
      {
        path: 'produtos',
        name: 'admin-products',
        component: () => import('@/pages/admin/produtos/index.vue'),
        meta: { title: 'Produtos - Admin Ergonlider' }
      },
      {
        path: 'produtos/novo',
        name: 'admin-product-create',
        component: () => import('@/pages/admin/produtos/novo.vue'),
        meta: { title: 'Novo Produto - Admin Ergonlider' }
      },
      {
        path: 'produtos/:id',
        name: 'admin-product-edit',
        component: () => import('@/pages/admin/produtos/[id].vue'),
        meta: { title: 'Editar Produto - Admin Ergonlider' }
      },
      {
        path: 'pedidos',
        name: 'admin-orders',
        component: () => import('@/pages/admin/pedidos/index.vue'),
        meta: { title: 'Pedidos - Admin Ergonlider' }
      },
      {
        path: 'pedidos/:id',
        name: 'admin-order-detail',
        component: () => import('@/pages/admin/pedidos/[id].vue'),
        meta: { title: 'Detalhes do Pedido - Admin Ergonlider' }
      },
      {
        path: 'usuarios',
        name: 'admin-users',
        component: () => import('@/pages/admin/usuarios/index.vue'),
        meta: { title: 'Usuários - Admin Ergonlider' }
      },
      {
        path: 'usuarios/:id',
        name: 'admin-user-detail',
        component: () => import('@/pages/admin/usuarios/[id].vue'),
        meta: { title: 'Detalhes do Usuário - Admin Ergonlider' }
      },
      {
        path: 'videos',
        name: 'admin-videos',
        component: () => import('@/pages/admin/videos/index.vue'),
        meta: { title: 'Vídeos - Admin Ergonlider' }
      },
      {
        path: 'videos/novo',
        name: 'admin-video-create',
        component: () => import('@/pages/admin/videos/novo.vue'),
        meta: { title: 'Novo Vídeo - Admin Ergonlider' }
      },
      {
        path: 'videos/:id',
        name: 'admin-video-edit',
        component: () => import('@/pages/admin/videos/[id].vue'),
        meta: { title: 'Editar Vídeo - Admin Ergonlider' }
      },
      {
        path: 'noticias',
        name: 'admin-news',
        component: () => import('@/pages/admin/noticias/index.vue'),
        meta: { title: 'Notícias - Admin Ergonlider' }
      },
      {
        path: 'noticias/novo',
        name: 'admin-news-create',
        component: () => import('@/pages/admin/noticias/novo.vue'),
        meta: { title: 'Nova Notícia - Admin Ergonlider' }
      },
      {
        path: 'noticias/:id',
        name: 'admin-news-edit',
        component: () => import('@/pages/admin/noticias/[id].vue'),
        meta: { title: 'Editar Notícia - Admin Ergonlider' }
      },
      {
        path: 'categorias-noticias',
        name: 'admin-news-categories',
        component: () => import('@/pages/admin/categorias-noticias/index.vue'),
        meta: { title: 'Categorias de Notícias - Admin Ergonlider' }
      },
      {
        path: 'cupons',
        name: 'admin-coupons',
        component: () => import('@/pages/admin/cupons/index.vue'),
        meta: { title: 'Cupons - Admin Ergonlider' }
      },
      {
        path: 'cupons/novo',
        name: 'admin-coupon-create',
        component: () => import('@/pages/admin/cupons/novo.vue'),
        meta: { title: 'Novo Cupom - Admin Ergonlider' }
      },
      {
        path: 'cupons/:id',
        name: 'admin-coupon-edit',
        component: () => import('@/pages/admin/cupons/[id].vue'),
        meta: { title: 'Editar Cupom - Admin Ergonlider' }
      },
      {
        path: 'canais',
        name: 'admin-channels',
        component: () => import('@/pages/admin/canais/index.vue'),
        meta: { title: 'Canais de Denúncia - Admin Ergonlider' }
      },
      {
        path: 'financeiro',
        name: 'admin-financial',
        component: () => import('@/pages/admin/financeiro/index.vue'),
        meta: { title: 'Financeiro - Admin Ergonlider' }
      },
      {
        path: 'configuracoes',
        name: 'admin-settings',
        component: () => import('@/pages/admin/configuracoes/index.vue'),
        meta: { title: 'Configurações - Admin Ergonlider' }
      },
    ]
  },

  // ========================================
  // PÁGINAS DE ERRO
  // ========================================
  {
    path: '/404',
    name: 'not-found',
    component: () => import('@/pages/404.vue'),
    meta: { title: 'Página não encontrada - Ergonlider' }
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404'
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth'
      }
    } else {
      return { top: 0, behavior: 'smooth' }
    }
  }
})

// Navigation Guards
router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  // Atualizar título da página
  document.title = (to.meta.title as string) || 'Ergonlider'

  // Verificar se precisa carregar estado de auth
  if (!authStore.initialized) {
    console.log('Router guard - initializing auth...')
    await authStore.initialize()
    console.log('Router guard - auth initialized, isAdmin:', authStore.isAdmin)
  } else if (authStore.isAuthenticated && !authStore.profile) {
    // Auth está inicializado mas profile não foi carregado ainda
    console.log('Router guard - fetching profile...')
    await authStore.fetchProfile()
    console.log('Router guard - profile fetched, isAdmin:', authStore.isAdmin)
  }

  // Rota requer autenticação
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    return next({
      name: 'login',
      query: { redirect: to.fullPath }
    })
  }

  // Rota apenas para visitantes (login, registro)
  if (to.meta.guestOnly && authStore.isAuthenticated) {
    return next({ name: 'dashboard' })
  }

  // Rota requer permissão de admin
  if (to.meta.requiresAdmin) {
    console.log('Admin route check:', {
      isAdmin: authStore.isAdmin,
      profile: authStore.profile,
      role: authStore.profile?.role
    })
    if (!authStore.isAdmin) {
      return next({ name: 'dashboard' })
    }
  }

  next()
})

export default router
